import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import type {
  CloseWorkspaceResult,
  DuplicateWorkspaceResult,
  RenameWorkspaceResult,
} from "./shared/workspace/workspace.repository";
import type { WorkspaceActiveView } from "./shared/workspace/workspaceSelection.controller";
import type { WorkspaceTemplateId } from "./shared/workspace/workspaceTemplates";
import type { WorkspaceTab, WorkspaceTabId } from "./Workspace.controller";

const renameWorkspaceSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Enter a workspace name")
    .max(100, "Name must be 100 characters or fewer"),
});

type RenameWorkspaceFormValues = z.infer<typeof renameWorkspaceSchema>;

const workspaceOperationError = "Unable to save workspace changes. Try again.";
const closeWorkspaceErrors = {
  "not-found":
    "This workspace was already removed in another window. It cannot be removed again.",
  "last-workspace": "This is now the last workspace and cannot be removed.",
} as const;
const renameWorkspaceNotFoundError =
  "This workspace was already removed in another window. It cannot be renamed.";
const duplicateWorkspaceNotFoundError =
  "This workspace was already removed in another window.";

export interface WorkspaceShellControllerDependencies {
  activeView: WorkspaceActiveView;
  tabs: ReadonlyArray<WorkspaceTab>;
  createWorkspace: (templateId?: WorkspaceTemplateId) => Promise<void>;
  duplicateWorkspace: (
    tabId: WorkspaceTabId,
  ) => Promise<DuplicateWorkspaceResult>;
  closeWorkspace: (tabId: WorkspaceTabId) => Promise<CloseWorkspaceResult>;
  renameWorkspace: (
    tabId: WorkspaceTabId,
    name: string,
  ) => Promise<RenameWorkspaceResult>;
  reorderWorkspace: (
    sourceTabId: WorkspaceTabId,
    targetTabId: WorkspaceTabId,
  ) => Promise<void>;
  resetToDefaults: () => Promise<void>;
  resetToBlank: () => Promise<void>;
}

export const useWorkspaceShellController = ({
  activeView,
  tabs,
  createWorkspace,
  duplicateWorkspace,
  closeWorkspace,
  renameWorkspace,
  reorderWorkspace,
  resetToDefaults,
  resetToBlank,
}: WorkspaceShellControllerDependencies) => {
  const [toolbarSlot, setToolbarSlot] = useState<HTMLDivElement | null>(null);
  const [titleLeadingSlot, setTitleLeadingSlot] =
    useState<HTMLDivElement | null>(null);
  const [renamingTabId, setRenamingTabId] = useState<WorkspaceTabId | null>(
    null,
  );
  const [pendingCloseId, setPendingCloseId] = useState<WorkspaceTabId | null>(
    null,
  );
  const [restartOpen, setRestartOpen] = useState(false);
  const [workspaceActionError, setWorkspaceActionError] = useState<
    string | null
  >(null);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [restartError, setRestartError] = useState<string | null>(null);

  const activeWorkspaceEntry =
    activeView.type === "workspace"
      ? tabs.find((tab) => tab.id === activeView.id)
      : undefined;
  const activeTabLabel =
    activeView.type === "overview"
      ? "Overview"
      : (activeWorkspaceEntry?.label ?? "");
  const renamingTab = tabs.find((tab) => tab.id === renamingTabId);
  const pendingCloseTab = tabs.find((tab) => tab.id === pendingCloseId);

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors },
  } = useForm<RenameWorkspaceFormValues>({
    resolver: zodResolver(renameWorkspaceSchema),
    defaultValues: { name: "" },
  });

  useEffect(() => {
    if (!renamingTabId) {
      return;
    }
    setFocus("name");
    const frame = requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active instanceof HTMLInputElement) {
        active.select();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [renamingTabId, setFocus]);

  const runWorkspaceAction = async (operation: () => Promise<void>) => {
    setWorkspaceActionError(null);
    try {
      await operation();
    } catch {
      setWorkspaceActionError(workspaceOperationError);
    }
  };

  const onCreateWorkspace = (templateId?: WorkspaceTemplateId) =>
    runWorkspaceAction(() => createWorkspace(templateId));

  const onDuplicateWorkspace = async (tabId: WorkspaceTabId) => {
    setWorkspaceActionError(null);
    try {
      const result = await duplicateWorkspace(tabId);
      if (result.status === "not-found") {
        setWorkspaceActionError(duplicateWorkspaceNotFoundError);
      }
    } catch {
      setWorkspaceActionError(workspaceOperationError);
    }
  };

  const onReorderTab = (
    sourceTabId: WorkspaceTabId,
    targetTabId: WorkspaceTabId,
  ) => runWorkspaceAction(() => reorderWorkspace(sourceTabId, targetTabId));

  const onBeginRename = (tab: WorkspaceTab) => {
    setWorkspaceActionError(null);
    setRenameError(null);
    setRenamingTabId(tab.id);
    reset({ name: tab.label });
  };

  const onRenameSubmit = handleSubmit(async ({ name }) => {
    if (!renamingTabId) {
      return;
    }
    if (!renamingTab) {
      setRenameError(renameWorkspaceNotFoundError);
      return;
    }
    if (name === renamingTab.label) {
      setRenamingTabId(null);
      return;
    }
    setRenameError(null);
    try {
      const result = await renameWorkspace(renamingTabId, name);
      if (result.status === "not-found") {
        setRenameError(renameWorkspaceNotFoundError);
      } else {
        setRenamingTabId(null);
      }
    } catch {
      setRenameError(workspaceOperationError);
    }
  });

  const onRenameOpenChange = (open: boolean) => {
    if (!open) {
      setRenamingTabId(null);
      setRenameError(null);
    }
  };

  const onRequestClose = (tabId: WorkspaceTabId) => {
    setWorkspaceActionError(null);
    setDeleteError(null);
    setPendingCloseId(tabId);
  };

  const onConfirmClose = async () => {
    if (!pendingCloseId) {
      return;
    }
    setDeleteError(null);
    try {
      const result = await closeWorkspace(pendingCloseId);
      if (result.status === "closed") {
        setPendingCloseId(null);
      } else {
        setDeleteError(closeWorkspaceErrors[result.status]);
      }
    } catch {
      setDeleteError(workspaceOperationError);
    }
  };

  const onCloseOpenChange = (open: boolean) => {
    if (!open) {
      setPendingCloseId(null);
      setDeleteError(null);
    }
  };

  const onRequestRestart = () => {
    setWorkspaceActionError(null);
    setRestartError(null);
    setRestartOpen(true);
  };

  const onRestartOpenChange = (open: boolean) => {
    setRestartOpen(open);
    if (!open) {
      setRestartError(null);
    }
  };

  const runRestart = async (operation: () => Promise<void>) => {
    setRestartError(null);
    try {
      await operation();
      setRestartOpen(false);
    } catch {
      setRestartError(workspaceOperationError);
    }
  };

  return {
    activeTabLabel,
    toolbarSlot,
    setToolbarSlot,
    titleLeadingSlot,
    setTitleLeadingSlot,
    renamingTabId,
    renamingTabLabel: renamingTab?.label ?? "this workspace",
    pendingCloseId,
    pendingCloseLabel: pendingCloseTab?.label ?? "workspace",
    restartOpen,
    workspaceActionError,
    renameError,
    deleteError,
    restartError,
    showPageActionError:
      !restartOpen && renamingTabId === null && pendingCloseId === null,
    renameField: register("name"),
    renameFieldError: errors.name?.message,
    onCreateWorkspace,
    onDuplicateWorkspace,
    onReorderTab,
    onBeginRename,
    onRenameSubmit,
    onRenameOpenChange,
    onRequestClose,
    onConfirmClose,
    onCloseOpenChange,
    onRequestRestart,
    onRestartOpenChange,
    onRestoreDefaults: () => runRestart(resetToDefaults),
    onStartBlank: () => runRestart(resetToBlank),
  };
};

export type WorkspaceShellController = ReturnType<
  typeof useWorkspaceShellController
>;
